import { useSession } from "@clerk/clerk-react";
import axios from "axios";
import { DiamondIcon, LoaderCircle } from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "sonner";
import {
  useCanvasStore,
  useSelectedNavTreeNode,
} from "@/components/Canvas/stores/canvasStore";
import {
  ChangePropChange,
  DELETE_PROP_CONSTANT,
} from "@/store/zustand/changeLedgerTypes";
import { useDesignCommandManager } from "@/store/zustand/designCommandManagerStore";
import { track } from "@/utils/events/tracker";
import { useCodebaseStore } from "../../store/zustand/codebaseStore";
import type { JsxElement } from "../../utils/jsx/jsxElementTypes";
import { TempoElement } from "../../utils/tempoElement";
import { getUnexpiredToken, useCanvasId } from "../../utils/utils";
import {
  ControlEditor,
  type ControlsSpec,
  type ControlValue,
  type ControlValues,
} from "../ControlEditor";
import { ClickableIcon } from "./TailwindStylingPanel/components/input";
import { SectionHeader } from "./TailwindStylingPanel/components/SectionHeader";

// TODO: Enforce the type of the prop value in the value you put in
// TODO: Queue up requests to parseComponentProps where we only send 1 at a time
// and any new requests just replace the queued up one
// TODO: Add some indication of what data type the prop is

const shouldFilterOutProp = (propName: string) => {
  return (
    propName.startsWith("aria-") ||
    propName.startsWith("data-") ||
    [
      "className",
      "ref",
      "key",
      "id",
      // TODO: Do this filter more manually via typescript types in the probe
      // conditionally on if its from an HTML compoennt type of element
      "form",
      "formNoValidate",
      "formAction",
      "formMethod",
      "formTarget",
      "formEncType",
      "formAction",
      "formMethod",
    ].includes(propName)
  );
};

// New raw prop format returned by backend (RemoteTsserver.getComponentProps)
type EntryValue = {
  type: string | number; // "string" | "number" | "boolean" | '"literal"' | number | "union"
  unionValues?: EntryValue[]; // present when type === "union"
};

type BackendPropEntry = {
  key: string;
  type: string | number; // may be primitive name or literal (quoted string or number) or "union"
  unionValues?: EntryValue[];
  optional: boolean;
};

const stripQuotes = (value: string): string =>
  value.startsWith('"') && value.endsWith('"') ? value.slice(1, -1) : value;

// Generate a type placeholder string from entry type information
const generateTypePlaceholder = (entry: BackendPropEntry): string => {
  if (entry.type === "union" && entry.unionValues) {
    const parts = entry.unionValues
      .map((v) => {
        if (typeof v.type === "number") return v.type.toString();
        if (typeof v.type === "string") {
          // Filter out undefined and null from placeholders
          if (v.type === "undefined" || v.type === "null") return "";
          if (v.type.startsWith('"') && v.type.endsWith('"')) return v.type;
          return v.type;
        }
        return "";
      })
      .filter(Boolean);
    return parts.join(" | ");
  }

  if (typeof entry.type === "number") {
    return entry.type.toString();
  }

  if (typeof entry.type === "string") {
    return entry.type;
  }

  return "";
};

// Extract raw values from navTreeNode.props shape: { [k]: { value, type } }
const extractRawValuesFromNodeProps = (
  nodeProps?: Record<string, { value: any; type?: string }>,
): Record<string, any> => {
  if (!nodeProps) return {};
  const out: Record<string, any> = {};
  Object.entries(nodeProps).forEach(([k, v]) => {
    out[k] = (v as any)?.value;
  });
  return out;
};

const normalizeUnionOptions = (values: EntryValue[] = []) => {
  const types: ("string" | "number" | "boolean" | "undefined" | "null")[] = [];
  const literals: {
    type: "string" | "number" | "boolean" | "undefined" | "null";
    value: string | number | boolean | undefined | null;
  }[] = [];
  for (const v of values) {
    if (typeof v.type === "number") {
      literals.push({ type: "number", value: v.type });
      continue;
    }
    if (typeof v.type === "string") {
      // String literal types will be quoted: "default"
      if (v.type.startsWith('"') && v.type.endsWith('"')) {
        literals.push({ type: "string", value: stripQuotes(v.type) });
      } else if (v.type === "true" || v.type === "false") {
        literals.push({ type: "boolean", value: v.type === "true" });
      } else if (v.type === "undefined") {
        literals.push({ type: "undefined", value: undefined });
      } else if (v.type === "null") {
        literals.push({ type: "null", value: null });
      } else {
        types.push(
          v.type as "string" | "number" | "boolean" | "undefined" | "null",
        );
      }
    }
  }
  return { types, literals };
};

// Convert backend entries to ControlEditor spec + initial values
const convertParsedPropsToControlSpec = (
  entries: BackendPropEntry[],
  currentValues: Record<string, any>,
): { controls: ControlsSpec; values: ControlValues } => {
  const controls: ControlsSpec = {};
  const values: ControlValues = {};

  for (const entry of entries) {
    const name = entry.key;
    const current = currentValues?.[name];

    /**
     * Union types are tricky, you could have stuff like in Button where its disabled?: boolean | undefined, or something
     * like "string | boolean | 1 | 2 | null".  To render a control for this, we
     * treat undefined | null as just making it optional or not, and unless its all
     */
    if (entry.type === "union") {
      const { types, literals } = normalizeUnionOptions(entry.unionValues);
      const isOptional =
        entry.optional ||
        literals.some((l) => l.type === "undefined" || l.type === "null");
      const nonEmptyLiterals = literals.filter(
        (l) => l.type !== "undefined" && l.type !== "null",
      );
      if (types.length === 0) {
        // Filter out empty strings from select options (Radix UI Select requires non-empty values)
        const validOptions = nonEmptyLiterals
          .map((l) => l.value as string | number | boolean)
          .filter((v) => v !== "");

        if (validOptions.length > 0) {
          controls[name] = {
            type: "select",
            options: validOptions,
            optional: isOptional,
          };
          values[name] =
            (current as ControlValue) ?? (validOptions[0] as ControlValue);
        } else {
          // If all options are empty strings, fall back to text input
          controls[name] = {
            type: "text",
            optional: isOptional,
            placeholder: generateTypePlaceholder(entry),
          };
          values[name] = (current as ControlValue) ?? "";
        }
        continue;
      }

      if (types.includes("string")) {
        // We have to support strings so show text control
        controls[name] = {
          type: "text",
          optional: isOptional,
          placeholder: generateTypePlaceholder(entry),
        };
        values[name] = (current as ControlValue) ?? "";
        continue;
      }
      if (
        types.every((t) => t === "number") &&
        !nonEmptyLiterals.some(
          (l) => l.type === "string" || l.type === "boolean",
        )
      ) {
        controls[name] = {
          type: "number",
          optional: isOptional,
          placeholder: generateTypePlaceholder(entry),
        };
        values[name] = (current as ControlValue) ?? 0;
        continue;
      }
      if (
        types.every((t) => t === "boolean") &&
        !nonEmptyLiterals.some(
          (l) => l.type === "string" || l.type === "number",
        )
      ) {
        controls[name] = { type: "boolean", optional: isOptional };
        values[name] = (current as ControlValue) ?? false;
        continue;
      }
      if (types.length > 0) {
        controls[name] = {
          type: "text",
          optional: isOptional,
          placeholder: generateTypePlaceholder(entry),
        };
        values[name] = (current as ControlValue) ?? "";
        continue;
      }
      continue;
    }

    if (typeof entry.type === "string") {
      // Literal string type (e.g., '"default"')
      if (entry.type.startsWith('"') && entry.type.endsWith('"')) {
        const literal = stripQuotes(entry.type);
        controls[name] = { type: "select", options: [literal] };
        values[name] = (current as ControlValue) ?? (literal as ControlValue);
        continue;
      }

      if (entry.type === "string") {
        controls[name] = {
          type: "text",
          placeholder: generateTypePlaceholder(entry),
        };
        values[name] = (current as string) ?? "";
        continue;
      }

      if (entry.type === "boolean") {
        controls[name] = { type: "boolean" };
        if (typeof current === "boolean") {
          values[name] = current;
        } else if (typeof current === "string") {
          values[name] = current.toLowerCase() === "true";
        } else {
          values[name] = false;
        }
        continue;
      }

      if (entry.type === "number") {
        controls[name] = {
          type: "number",
          placeholder: generateTypePlaceholder(entry),
        };
        if (typeof current === "number") {
          values[name] = current;
        } else if (current != null && current !== "") {
          const n = Number(current as any);
          values[name] = Number.isNaN(n) ? 0 : n;
        } else {
          values[name] = 0;
        }
        continue;
      }

      // Fallback
      controls[name] = {
        type: "text",
        placeholder: generateTypePlaceholder(entry),
      };
      values[name] = (current as string) ?? "";
      continue;
    }

    if (typeof entry.type === "number") {
      // Numeric literal
      controls[name] = {
        type: "number",
        placeholder: generateTypePlaceholder(entry),
      };
      if (typeof current === "number") {
        values[name] = current;
      } else if (current != null && current !== "") {
        const n = Number(current as any);
        values[name] = Number.isNaN(n) ? entry.type : n;
      } else {
        values[name] = entry.type;
      }
    }
  }

  return { controls, values };
};

// Hook to fetch, cache, and update component props
const useComponentProps = (shouldFetch: boolean) => {
  const { selectedElement, selectedNavTreeNode } = useSelectedNavTreeNode();
  const codebaseId = selectedElement?.codebaseId;
  const selectedTreeElement: JsxElement | undefined = useCodebaseStore((s) =>
    codebaseId ? s.treeElementLookup[codebaseId] : undefined,
  );

  const canvasId = useCanvasId();
  const { session } = useSession();

  // Cached controls in store
  const cachedSpec = useCodebaseStore((s) =>
    codebaseId ? s.cachedControlsSpecs[codebaseId] : undefined,
  );
  const setCachedSpecs = useCodebaseStore((s) => s.setCachedControls);

  // Store controls and values as records keyed by codebaseId
  const [controlsRecord, setControlsRecord] = useState<
    Record<string, ControlsSpec>
  >({});
  // Optimistic overlay of prop values keyed by element key
  const [optimisticValuesByElementKey, setOptimisticValuesByElementKey] =
    useState<Record<string, ControlValues>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Controls come from cache or fetch; values are derived from nav tree + optimistic overlay
  const controls = codebaseId
    ? controlsRecord[codebaseId] || cachedSpec || null
    : null;
  const elementKey = selectedNavTreeNode?.tempoElement?.getKey() || "";
  const nodeRawValues = extractRawValuesFromNodeProps(
    (selectedNavTreeNode?.props as any) || {},
  );
  const values: ControlValues = {
    ...nodeRawValues,
    ...(optimisticValuesByElementKey[elementKey || ""] || {}),
  };

  const displayName = selectedTreeElement?.componentName;

  // Fetch only when expanded (lazy); allow refetch when selection changes
  useEffect(() => {
    if (!shouldFetch) return;
    const fetchProps = async () => {
      // componentSourceImport doesn't have the .tsx extension
      let filepath =
        selectedTreeElement?.componentSourceImport?.filename ||
        selectedTreeElement?.filename ||
        "";
      if (!filepath.endsWith(".tsx") && filepath.startsWith("/")) {
        filepath = filepath + ".tsx";
      }

      if (!filepath || !selectedTreeElement?.componentName || !codebaseId) {
        // Clear controls and values for this codebaseId if it exists
        if (codebaseId) {
          setControlsRecord((prev) => ({ ...prev, [codebaseId]: {} }));
        }
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const supabaseJWT = await getUnexpiredToken(session);

        const componentName =
          selectedTreeElement?.componentSourceImport?.name ||
          selectedTreeElement.componentName;
        const isDefault =
          selectedTreeElement?.componentSourceImport?.importType ===
            "ImportDefaultSpecifier" || selectedTreeElement?.defaultExport;
        const response = await axios.post(
          `${
            import.meta.env.VITE_BACKEND_BASE_URL
          }/canvases/${canvasId}/parseAndMutate/parse/parseComponentProps`,
          { filepath, componentName, isDefault, enableDebugLogs: true },
          { headers: { Authorization: `Bearer ${supabaseJWT}` } },
        );

        const entries = (response.data as BackendPropEntry[]).filter(
          (e) => !shouldFilterOutProp(e.key),
        );
        const { controls: spec } = convertParsedPropsToControlSpec(
          entries,
          extractRawValuesFromNodeProps(
            (selectedNavTreeNode?.props as any) || {},
          ),
        );

        // Store in records keyed by codebaseId
        setControlsRecord((prev) => ({ ...prev, [codebaseId]: spec }));
        setCachedSpecs(codebaseId, spec);
      } catch (e) {
        console.error("Failed to load component props:", e);
        setError("Failed to load component props");
        // Clear for this codebaseId on error
        if (codebaseId) {
          setControlsRecord((prev) => ({ ...prev, [codebaseId]: {} }));
        }
      } finally {
        setLoading(false);
      }
    };
    fetchProps();
  }, [
    selectedTreeElement?.componentName,
    selectedTreeElement?.filename,
    canvasId,
    session,
    codebaseId,
    shouldFetch,
    elementKey,
  ]);

  // Change handler
  const handleChange = useCallback(
    (changes: Record<string, ControlValue>) => {
      const propName = Object.keys(changes)[0];
      let value = changes[propName];
      if (!propName || !codebaseId) return;

      // Exit early if value is unchanged
      const currentValue = values[propName];
      if (currentValue === value) {
        return;
      } else if (currentValue === undefined && !value) {
        return;
      }

      // optimistic update for this specific element
      setOptimisticValuesByElementKey((prev) => ({
        ...prev,
        [elementKey]: { ...(prev[elementKey] || {}), [propName]: value },
      }));

      track("PROP_MANAGER_EDIT", {
        propType: controls?.[propName]?.type,
      });

      // Tell the backend to just delete the prop if its optional and falsey to keep the code clean
      if (!value && controls?.[propName]?.optional) {
        value = DELETE_PROP_CONSTANT;
      }

      try {
        useDesignCommandManager.getState().executeCommand(
          new ChangePropChange({
            codebaseIdToChange: codebaseId,
            propName,
            propValue: value,
          }),
        );
      } catch (e) {
        console.error("Failed to change prop:", e);
        // Rollback optimistic update
        setOptimisticValuesByElementKey((prev) => ({
          ...prev,
          [elementKey]: {
            ...(prev[elementKey] || {}),
            [propName]: currentValue,
          },
        }));
        toast.error("Failed to update prop");
      }
    },
    [codebaseId, elementKey],
  );

  // Clear optimistic entries when navTreeNode.props catches up
  useEffect(() => {
    const optimistic = optimisticValuesByElementKey[elementKey] || {};
    if (!selectedNavTreeNode || !selectedNavTreeNode.props) return;
    const nodeProps = selectedNavTreeNode.props as Record<
      string,
      { value: any }
    >;
    const remaining: Record<string, ControlValue> = {};
    let changed = false;
    Object.keys(optimistic).forEach((k) => {
      if (nodeProps[k]?.value === optimistic[k]) {
        changed = true;
      } else {
        remaining[k] = optimistic[k];
      }
    });
    if (changed) {
      setOptimisticValuesByElementKey((prev) => ({
        ...prev,
        [elementKey]: remaining,
      }));
    }
  }, [selectedNavTreeNode?.props, elementKey]);

  return {
    controls,
    values,
    displayName,
    loading,
    error,
    handleChange,
  } as const;
};

// Component
export const PropManager: React.FC = () => {
  // Determine current codebaseId to preserve open state per component
  const { selectedElement } = useSelectedNavTreeNode();
  const currentCodebaseId = selectedElement?.codebaseId || "";

  // Persist open/close per codebaseId within this component lifecycle
  const [openByCodebaseId, setOpenByCodebaseId] = useState<
    Record<string, boolean>
  >({});
  const open = currentCodebaseId
    ? Boolean(openByCodebaseId[currentCodebaseId])
    : false;

  const { controls, values, displayName, loading, error, handleChange } =
    useComponentProps(open);
  const hasProps = controls && Object.keys(controls).length > 0;

  return (
    <div className="props-manager" data-testid="props-manager">
      <SectionHeader
        title={displayName || "Component"}
        data-testid="props-manager-section"
        isExpanded={open && controls !== null}
        onToggle={() => {
          if (!currentCodebaseId) return;
          if (!open) {
            track("PROP_MANAGER_OPEN");
          }
          setOpenByCodebaseId((prev) => ({
            ...prev,
            [currentCodebaseId]: !prev[currentCodebaseId],
          }));
        }}
        isActive={hasProps || false}
        titleClassName="text-[var(--primary-n-600)]"
        icon={<DiamondIcon className="w-3 h-3 shrink-0" />}
        rightIconOverride={
          loading ? (
            <LoaderCircle className="w-3 h-3 shrink-0 animate-spin text-slate-600" />
          ) : undefined
        }
      >
        <div className="controls-manager space-y-2">
          {error ? (
            <div className="text-[10px] text-red-400">{error}</div>
          ) : loading && controls === null ? (
            <div className="text-[10px] text-[#9F9FA4]">Loading props...</div>
          ) : !hasProps ? (
            <div className="text-[10px] text-[#9F9FA4]">
              No editable props found for this component
            </div>
          ) : (
            <ControlEditor
              controls={controls}
              values={values}
              onChange={handleChange}
            />
          )}
        </div>
      </SectionHeader>
    </div>
  );
};

export default PropManager;
